import EventBus from '@zageno/event-bus';
import { useCurrentUser } from '@zageno/profile';
import { LDClient } from 'launchdarkly-js-client-sdk';
import { useEffect, useRef } from 'react';

import { FeatureFlagEvents } from '../events';
import { anonymousContext, initLDClient } from '../utils';

export interface FeatureFlags {
  // Real name: eng.cur.lists.add_all_to_cart
  engCurListsAddAllToCart: boolean;
  // Real name: eng.cur.errors.display-stacktrace
  engCurErrorsDisplayStacktrace: boolean;
  // Real name: prod.cur.dashboard.dashboard-notification
  prodCurDashboardDashboardNotification: {
    isActive: boolean;
    badgeBackground?: string;
    badgeText?: string;
    badgeTextColor?: string;
    content?: string;
    heading?: string;
    url?: string;
  };
  // Real name: prod.cur.search.search-ui-version
  prodCurSearchSearchUiVersion: '1' | '2';
}

const toCamelCase = (dashedString: string) => dashedString.replace(/[-_]([a-z])/g, c => c[1].toUpperCase());

const fetchFeatureFlags = (flags: Record<string, unknown>) =>
  Object.entries(flags).reduce((acc, [key, value]) => {
    const featureFlagName = toCamelCase(key.replace(/\./g, '_'));

    return {
      ...acc,
      [featureFlagName]: value,
    };
  }, {} as FeatureFlags);

export function FeatureFlag() {
  const ldClientRef = useRef<LDClient>(null);
  const featureFlagsRef = useRef<Partial<FeatureFlags>>({});
  const user = useCurrentUser();

  useEffect(() => {
    function handleOnChange() {
      featureFlagsRef.current = fetchFeatureFlags(ldClientRef.current.allFlags());
      EventBus.publish<FeatureFlags>(FeatureFlagEvents.CHANGE, { data: featureFlagsRef.current as FeatureFlags });
    }

    if (!ldClientRef.current) {
      ldClientRef.current = initLDClient(handleOnChange, ldClientRef);
    }

    ldClientRef.current.on('change', handleOnChange);

    // This keeps the subscription enabled until the end the application
    const subscriptionId = EventBus.subscribe<FeatureFlags>(FeatureFlagEvents.CHANGE, () => {});

    return () => {
      ldClientRef.current.off('change', handleOnChange);
      EventBus.unsubscribe(subscriptionId);
    };
  }, []);

  useEffect(() => {
    if (user == null) {
      return;
    }

    if (user.logged_in) {
      void ldClientRef.current?.identify({
        anonymous: false,
        kind: 'user',
        country: user.country_name,
        email: user.email,
        firstName: user.first_name,
        lastName: user.last_name,
        key: user.user_id,
        name: `${user.first_name} ${user.last_name}`,
        custom: {
          org_id: user.org_id,
          org_name: user.org_name,
          org_key: user.org_key,
          is_hijacked: user.is_hijacked,
        },
      });
    } else {
      void ldClientRef.current?.identify(anonymousContext);
    }
  }, [user]);

  return null;
}
